import React, { useState, useEffect } from 'react';

import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Badge,
  Backdrop,
  Button,
  Checkbox,
} from '@mui/material';
import HotelSharpIcon from '@mui/icons-material/HotelSharp';
import BathtubSharpIcon from '@mui/icons-material/BathtubSharp';
import PeopleSharpIcon from '@mui/icons-material/PeopleSharp';
import StarRateSharpIcon from '@mui/icons-material/StarRateSharp';
import { styled } from '@mui/material/styles';

import { COLOR_PRIMARY, POINTER_GREEN, COLOR_PRIMARY_DARK, COLOR_PRIMARY_LIGHT } from 'src/const';
import airbnb from 'src/App/assets/airbnb.svg';
import { Listing, Rate } from 'src/interfaces/idashboard';
import { IndividualCompListingModal } from '../../Modals';
import { getFuture30DaysKeyMetrics } from 'src/utils/functions';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -3,
    top: 20,
    border: `2px solid ${POINTER_GREEN}`,
    padding: '0 4px',
    backgroundColor: 'white',
    color: POINTER_GREEN,
  },
}));

const CompListingCard = ({
  listing,
  rates,
  hoveredCompListingID,
  userListing,
  lastCalDate,
  editMode,
  deletedCompListingIDs,
  setDeletedCompListingIDs,
}: {
  listing: Listing;
  rates: {
    [date: string]: Rate;
  };
  hoveredCompListingID: string;
  userListing;
  lastCalDate: string;
  editMode: boolean;
  deletedCompListingIDs: string[];
  setDeletedCompListingIDs: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const {
    avgDailyRateNext30Days,
    occupancyRateNext30Days,
    avgAvailableDailyRateNext30Days,
    avgBookedDailyRateNext30Days,
  } = getFuture30DaysKeyMetrics(rates);
  const [indivCompModalOpen, setIndivCompModalOpen] = useState<boolean>(false);

  const handleCompListingCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setDeletedCompListingIDs(prev =>
        prev.includes(`${listing.listingID}`) ? prev : [...prev, `${listing.listingID}`]
      );
    } else {
      setDeletedCompListingIDs(prev => prev.filter(id => id !== `${listing.listingID}`));
    }
  };

  return (
    <>
      <Card sx={{ width: 250, height: 400, position: 'relative' }}>
        <CardMedia
          component="img"
          alt={listing.name}
          height="120"
          image={listing.thumbnail_url}
          title={listing.name}
        />

        {/* Origin Tag */}
        <div
          style={{
            background: '#fff',
            padding: '5px 15px',
            borderRadius: 100,
            position: 'absolute',
            top: '10px',
            left: '10px',
          }}
        >
          <img src={airbnb} style={{ height: '15px', marginTop: '5px' }} />
        </div>

        {/* Checkbox */}
        {editMode && (
          <div
            style={{
              background: 'transparent',
              position: 'absolute',
              top: '10px',
              right: '10px',
              padding: 0,
            }}
          >
            <Checkbox
              checked={deletedCompListingIDs.includes(`${listing.listingID}`)}
              onChange={handleCompListingCheckboxChange}
              sx={{
                color: '#fff',
                backgroundColor: '#fff',
                padding: 0,
                borderRadius: 1,
                '&.Mui-checked': {
                  color: COLOR_PRIMARY_DARK,
                },
                '&.Mui-checked:hover': {
                  backgroundColor: '#fff',
                },
                '&:hover': {
                  backgroundColor: '#fff',
                },
              }}
            />
          </div>
        )}

        <CardContent sx={{ width: '100%' }}>
          <Grid container direction="column" item xs={12} spacing={1} sx={{ width: '100%' }}>
            {/* title */}
            <Grid item>
              <Typography
                variant="h6"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  height: '3.6em',
                }}
              >
                {listing.name}
              </Typography>
            </Grid>

            {/* basic details badges */}
            <Grid item container direction="row" justifyContent="space-between">
              <Grid item xs={3}>
                <StyledBadge badgeContent={listing.bedrooms} color="secondary">
                  <HotelSharpIcon sx={{ color: POINTER_GREEN }} />
                </StyledBadge>
              </Grid>

              <Grid item xs={3}>
                <StyledBadge badgeContent={listing.bathrooms} color="secondary">
                  <BathtubSharpIcon sx={{ color: POINTER_GREEN }} />
                </StyledBadge>
              </Grid>

              <Grid item xs={3}>
                <StyledBadge badgeContent={listing.accommodates} color="secondary">
                  <PeopleSharpIcon sx={{ color: POINTER_GREEN }} />
                </StyledBadge>
              </Grid>

              <Grid item xs={3}>
                {listing.review_scores_rating > 0 && (
                  <StyledBadge badgeContent={listing.review_scores_rating} color="secondary">
                    <StarRateSharpIcon sx={{ color: POINTER_GREEN }} />
                  </StyledBadge>
                )}
              </Grid>
            </Grid>

            {/* hosted by and reviews */}
            <Grid item xs={12} container direction="row" justifyContent="space-between">
              <Grid
                container
                item
                xs={7}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  color="textSecondary"
                  variant="caption"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  Hosted By,{' '}
                  <span
                    style={{
                      color: '#000',
                    }}
                  >
                    {listing.host_name}{' '}
                  </span>
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography color="textSecondary" variant="caption">
                  <span
                    style={{
                      color: '#000',
                    }}
                  >
                    {listing.visible_review_count}
                  </span>{' '}
                  {listing.visible_review_count > 1 ? 'reviews' : 'review'}
                </Typography>
              </Grid>
            </Grid>

            {/* performance */}
            <Grid item container direction="column">
              <Typography color="textPrimary" variant="subtitle2" sx={{ marginBottom: 1 }}>
                Next 30 Days Insights
              </Typography>

              <Grid
                item
                xs={12}
                container
                direction="row"
                justifyContent="space-between"
                // alignItems="flex-start"
              >
                <Grid item xs={5}>
                  <Typography
                    color="textPrimary"
                    align="center"
                    sx={{
                      color: POINTER_GREEN,
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                    }}
                  >
                    ${avgDailyRateNext30Days}
                  </Typography>
                  <Typography color="textSecondary" variant="subtitle2" align="center">
                    Average Daily Rate
                  </Typography>
                </Grid>

                <Divider orientation="vertical" flexItem />

                <Grid item xs={5}>
                  <Typography
                    color="textPrimary"
                    align="center"
                    sx={{
                      color: POINTER_GREEN,
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {occupancyRateNext30Days}%
                  </Typography>
                  <Typography color="textSecondary" variant="subtitle2" align="center">
                    Occupancy Rate
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>

        {!editMode && listing.listingID === hoveredCompListingID && (
          <Backdrop
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              color: '#fff',
              zIndex: 100,
            }}
            open={true}
          >
            <Button
              sx={{
                background: COLOR_PRIMARY_DARK,
                borderRadius: 2,
                color: 'white',
                padding: '5px 15px',
                width: 150,
                height: 40,
                boxShadow: `3px 3px 2px ${COLOR_PRIMARY_LIGHT}50`,
                '&:hover': {
                  background: COLOR_PRIMARY,
                },
                fontSize: '1.1rem',
              }}
              onClick={() => {
                setIndivCompModalOpen(true);
              }}
            >
              Analyze
            </Button>
          </Backdrop>
        )}
      </Card>

      {indivCompModalOpen && (
        <IndividualCompListingModal
          open={indivCompModalOpen}
          onClose={setIndivCompModalOpen}
          listing={listing}
          userListing={userListing}
          rates={rates}
          lastCalDate={lastCalDate}
        />
      )}
    </>
  );
};

export default CompListingCard;
